/**
 * TourPreview v3.0.0 - Fully isolated tour test with EMBEDDED tour logic
 * 
 * This page bypasses ALL auth, onboarding, and route guards.
 * It includes an EMBEDDED tour that doesn't depend on FirstTimeAppTour's
 * route/auth restrictions - pure isolation for debugging.
 * 
 * Access: /tour-preview
 */

import * as React from 'react';
const { useEffect, useState, useCallback, useRef } = React;
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { 
  Play, RotateCcw, Home, Bug, ChevronRight, X,
  Star, Sparkles, Menu, MessageCircle, ClipboardList,
  UtensilsCrossed, TreePine, Activity, Users, Heart, Settings,
  CheckCircle, AlertTriangle, Stethoscope
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Debug timestamp for when JS loaded
const JS_LOADED_TIME = new Date().toISOString();

// Embedded tour steps - matches FirstTimeAppTour exactly (14 steps)
const TOUR_STEPS = [
  { id: 'welcome', target: '', title: 'Welcome to Therappi', icon: Star, position: 'center' as const },
  { id: 'guide-button', target: '[data-tour="guide-button"]', title: 'Your Personal Guide', icon: Sparkles, position: 'bottom' as const },
  { id: 'menu-button', target: '[data-tour="menu-button"]', title: 'Your Navigation Hub', icon: Menu, position: 'top' as const },
  { id: 'favourites', target: '[data-tour="favorites-section"]', title: 'Your Favourites', icon: Star, position: 'right' as const },
  { id: 'therappi-ai', target: '[data-tour="therappi-ai-link"]', title: 'Meet Therappi AI', icon: MessageCircle, position: 'right' as const },
  { id: 'daily-checkin', target: '[data-tour="checkin-link"]', title: 'Check In, Day & Night', icon: ClipboardList, position: 'left' as const },
  { id: 'smart-food-scanner', target: '[data-tour="nutrition-link"]', title: 'Scan Your Food', icon: UtensilsCrossed, position: 'left' as const },
  { id: 'sanctuary', target: '[data-tour="sanctuary-link"]', title: 'Your Healing Sanctuary', icon: TreePine, position: 'left' as const },
  { id: 'whoop-connection', target: '[data-tour="whoop-link"]', title: 'Connect Your Body', icon: Activity, position: 'left' as const },
  { id: 'community', target: '[data-tour="community-link"]', title: 'Find Your People', icon: Users, position: 'left' as const },
  { id: 'therapist-connection', target: '[data-tour="clinician-link"]', title: 'Connect with Your Therapist', icon: Stethoscope, position: 'left' as const },
  { id: 'relational', target: '[data-tour="relational-link"]', title: 'Relationships Matter', icon: Heart, position: 'left' as const },
  { id: 'account-features', target: '[data-tour="settings-button"]', title: 'My Account', icon: Settings, position: 'left' as const },
  { id: 'ready', target: '', title: "You're All Set!", icon: Star, position: 'center' as const },
];

// Mock navigation elements that match the real app's data-tour attributes
const MockNavigationElements = () => (
  <div className="fixed left-0 top-0 bottom-0 w-64 bg-card border-r border-border p-4 pt-20 overflow-y-auto z-10">
    {/* Mock menu button (burger) - top left */}
    <div 
      data-tour="menu-button"
      className="fixed top-4 left-4 w-10 h-10 rounded-lg bg-muted border border-border flex items-center justify-center cursor-pointer hover:bg-muted/80"
    >
      <Menu className="w-5 h-5" />
    </div>
    
    {/* Mock guide button (sparkle) - bottom right */}
    <div 
      data-tour="guide-button"
      className="fixed bottom-24 right-4 w-14 h-14 rounded-full bg-primary border-2 border-primary-foreground flex items-center justify-center shadow-lg"
    >
      <Sparkles className="w-6 h-6 text-primary-foreground" />
    </div>
    
    {/* Favourites section */}
    <div 
      data-tour="favorites-section"
      className="mb-4 p-3 rounded-lg bg-muted/50 border border-border"
    >
      <div className="flex items-center gap-2 text-sm font-medium">
        <Star className="w-4 h-4 text-yellow-500" />
        Favourites
      </div>
    </div>
    
    {/* Menu items */}
    <div className="space-y-2">
      <div data-tour="therappi-ai-link" className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 border border-border hover:bg-muted/50 cursor-pointer">
        <MessageCircle className="w-5 h-5 text-primary" />
        <span className="text-sm">Therappi® AI</span>
      </div>
      <div data-tour="checkin-link" className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 border border-border hover:bg-muted/50 cursor-pointer">
        <ClipboardList className="w-5 h-5 text-blue-500" />
        <span className="text-sm">Daily Check-in</span>
      </div>
      <div data-tour="nutrition-link" className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 border border-border hover:bg-muted/50 cursor-pointer">
        <UtensilsCrossed className="w-5 h-5 text-orange-500" />
        <span className="text-sm">Food Scanner</span>
      </div>
      <div data-tour="sanctuary-link" className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 border border-border hover:bg-muted/50 cursor-pointer">
        <TreePine className="w-5 h-5 text-green-500" />
        <span className="text-sm">Sanctuary</span>
      </div>
      <div data-tour="whoop-link" className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 border border-border hover:bg-muted/50 cursor-pointer">
        <Activity className="w-5 h-5 text-red-500" />
        <span className="text-sm">WHOOP</span>
      </div>
      <div data-tour="community-link" className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 border border-border hover:bg-muted/50 cursor-pointer">
        <Users className="w-5 h-5 text-purple-500" />
        <span className="text-sm">Community</span>
      </div>
      <div data-tour="clinician-link" className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 border border-border hover:bg-muted/50 cursor-pointer">
        <Stethoscope className="w-5 h-5 text-emerald-500" />
        <span className="text-sm">Connect with Therapist</span>
      </div>
      <div data-tour="relational-link" className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 border border-border hover:bg-muted/50 cursor-pointer">
        <Heart className="w-5 h-5 text-pink-500" />
        <span className="text-sm">Relational</span>
      </div>
      <div data-tour="settings-button" className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 border border-border hover:bg-muted/50 cursor-pointer">
        <Settings className="w-5 h-5 text-muted-foreground" />
        <span className="text-sm">My Account</span>
      </div>
    </div>
  </div>
);

// Spotlight overlay component
const SpotlightOverlay: React.FC<{
  targetSelector: string;
  isCenter: boolean;
}> = ({ targetSelector, isCenter }) => {
  const [rect, setRect] = useState<DOMRect | null>(null);

  useEffect(() => {
    if (isCenter || !targetSelector) {
      setRect(null);
      return;
    }

    const element = document.querySelector(targetSelector);
    if (element) {
      const r = element.getBoundingClientRect();
      setRect(r);
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [targetSelector, isCenter]);

  if (isCenter || !rect) {
    // Full dark overlay for center steps
    return (
      <div className="fixed inset-0 bg-black/70 z-40 pointer-events-none" />
    );
  }

  // Spotlight cutout effect
  const padding = 12;
  return (
    <div className="fixed inset-0 z-40 pointer-events-none">
      <svg className="w-full h-full">
        <defs>
          <mask id="spotlight-mask">
            <rect width="100%" height="100%" fill="white" />
            <rect 
              x={rect.left - padding} 
              y={rect.top - padding} 
              width={rect.width + padding * 2} 
              height={rect.height + padding * 2} 
              rx="8"
              fill="black" 
            />
          </mask>
        </defs>
        <rect 
          width="100%" 
          height="100%" 
          fill="rgba(0,0,0,0.75)" 
          mask="url(#spotlight-mask)" 
        />
      </svg>
      {/* Highlight ring around target */}
      <div 
        className="absolute border-2 border-primary rounded-lg animate-pulse"
        style={{
          left: rect.left - padding,
          top: rect.top - padding,
          width: rect.width + padding * 2,
          height: rect.height + padding * 2,
        }}
      />
    </div>
  );
};

// Tour card component
const TourCard: React.FC<{
  step: typeof TOUR_STEPS[0];
  stepIndex: number;
  totalSteps: number;
  onNext: () => void;
  onPrev: () => void;
  onClose: () => void;
  isFirst: boolean;
  isLast: boolean;
}> = ({ step, stepIndex, totalSteps, onNext, onPrev, onClose, isFirst, isLast }) => {
  const Icon = step.icon;
  
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9, y: 20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.9, y: 20 }}
      className="fixed z-[9999] w-[90vw] max-w-sm left-1/2 -translate-x-1/2"
      style={{
        top: step.position === 'center' ? '50%' : 
             step.position === 'top' ? '60%' : '20%',
        transform: step.position === 'center' 
          ? 'translate(-50%, -50%)' 
          : 'translateX(-50%)'
      }}
    >
      <Card className="p-5 bg-card border-2 border-primary shadow-2xl">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
              <Icon className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h3 className="font-bold text-lg">{step.title}</h3>
              <span className="text-xs text-muted-foreground">
                Step {stepIndex + 1} of {totalSteps}
              </span>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="p-1 hover:bg-muted rounded"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        
        {/* Progress bar */}
        <div className="h-1 bg-muted rounded-full mb-4 overflow-hidden">
          <div 
            className="h-full bg-primary transition-all duration-300"
            style={{ width: `${((stepIndex + 1) / totalSteps) * 100}%` }}
          />
        </div>
        
        {/* Navigation */}
        <div className="flex gap-2">
          {!isFirst && (
            <Button variant="outline" onClick={onPrev} className="flex-1">
              Back
            </Button>
          )}
          <Button onClick={isLast ? onClose : onNext} className="flex-1">
            {isLast ? 'Finish Tour' : 'Next'}
            {!isLast && <ChevronRight className="w-4 h-4 ml-1" />}
          </Button>
        </div>
      </Card>
    </motion.div>
  );
};

// Target audit function
const auditTargets = (): { step: string; target: string; found: boolean }[] => {
  return TOUR_STEPS.map(step => ({
    step: step.id,
    target: step.target || '(center - no target)',
    found: step.target ? !!document.querySelector(step.target) : true
  }));
};

export default function TourPreview() {
  const [tourActive, setTourActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [clickCount, setClickCount] = useState(0);
  const [lastError, setLastError] = useState<string | null>(null);
  const [showDebugPanel, setShowDebugPanel] = useState(true);
  const [targetAudit, setTargetAudit] = useState<{ step: string; target: string; found: boolean }[] | null>(null);
  const [logs, setLogs] = useState<string[]>([]);
  const navigate = useNavigate();
  
  const addLog = useCallback((msg: string) => {
    const timestamp = new Date().toLocaleTimeString();
    console.log(`[TourPreview] ${msg}`);
    setLogs(prev => [...prev.slice(-20), `${timestamp}: ${msg}`]);
  }, []);

  // Log component mount
  useEffect(() => {
    addLog('=== TourPreview v3.0.0 MOUNTED ===');
    addLog(`JS_LOADED: ${JS_LOADED_TIME}`);
    addLog(`URL: ${window.location.href}`);
    
    // Run initial target audit
    setTimeout(() => {
      const audit = auditTargets();
      setTargetAudit(audit);
      const found = audit.filter(a => a.found).length;
      addLog(`Target audit: ${found}/${audit.length} found`);
    }, 100);

    return () => {
      addLog('=== TourPreview UNMOUNTING ===');
    };
  }, [addLog]);

  const startTour = useCallback(() => {
    addLog('>>> START TOUR CLICKED <<<');
    setClickCount(prev => prev + 1);
    
    try {
      // Run target audit first
      const audit = auditTargets();
      setTargetAudit(audit);
      
      const missingTargets = audit.filter(a => !a.found);
      if (missingTargets.length > 0) {
        addLog(`WARNING: ${missingTargets.length} missing targets`);
        missingTargets.forEach(m => addLog(`  Missing: ${m.step} -> ${m.target}`));
      }
      
      addLog('Starting tour...');
      setTourActive(true);
      setCurrentStep(0);
      addLog('Tour started! Step 1/13');
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      addLog(`ERROR: ${msg}`);
      setLastError(msg);
    }
  }, [addLog]);

  const nextStep = useCallback(() => {
    if (currentStep < TOUR_STEPS.length - 1) {
      const newStep = currentStep + 1;
      setCurrentStep(newStep);
      addLog(`Step ${newStep + 1}/${TOUR_STEPS.length}: ${TOUR_STEPS[newStep].id}`);
    }
  }, [currentStep, addLog]);

  const prevStep = useCallback(() => {
    if (currentStep > 0) {
      const newStep = currentStep - 1;
      setCurrentStep(newStep);
      addLog(`Step ${newStep + 1}/${TOUR_STEPS.length}: ${TOUR_STEPS[newStep].id}`);
    }
  }, [currentStep, addLog]);

  const closeTour = useCallback(() => {
    addLog('Tour closed');
    setTourActive(false);
    setCurrentStep(0);
  }, [addLog]);

  const resetTour = useCallback(() => {
    addLog('>>> RESET CLICKED <<<');
    setClickCount(prev => prev + 1);
    setTourActive(false);
    setCurrentStep(0);
    setLastError(null);
    setTargetAudit(null);
    addLog('Tour reset complete');
  }, [addLog]);

  const runAudit = useCallback(() => {
    addLog('>>> AUDIT CLICKED <<<');
    setClickCount(prev => prev + 1);
    const audit = auditTargets();
    setTargetAudit(audit);
    addLog(`Audit complete: ${audit.filter(a => a.found).length}/${audit.length} targets found`);
  }, [addLog]);

  const step = TOUR_STEPS[currentStep];

  return (
    <div className="min-h-screen bg-background">
      {/* Mock navigation - the sidebar with all tour targets */}
      <MockNavigationElements />
      
      {/* Main content area */}
      <div className="ml-64 p-4">
        {/* Debug Panel */}
        {showDebugPanel && (
          <Card className="mb-4 p-3 bg-black/95 text-green-400 font-mono text-xs max-h-64 overflow-auto">
            <div className="flex justify-between items-center mb-2">
              <span className="font-bold text-green-300">🐛 Debug Panel v3.0.0</span>
              <button 
                onClick={() => setShowDebugPanel(false)}
                className="text-red-400 hover:text-red-300"
              >
                ✕
              </button>
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 mb-2">
              <div>✅ JS Loaded: {JS_LOADED_TIME}</div>
              <div>🚀 Tour Active: {tourActive ? <span className="text-green-300 font-bold">YES</span> : <span className="text-red-400">NO</span>}</div>
              <div>📊 Step: {currentStep + 1} / {TOUR_STEPS.length}</div>
              <div>🖱️ Clicks: {clickCount}</div>
            </div>
            {lastError && (
              <div className="text-red-400 mb-2">❌ Error: {lastError}</div>
            )}
            <div className="border-t border-green-800 pt-2 mt-2">
              <div className="font-bold mb-1">📜 Activity Log:</div>
              {logs.slice(-8).map((log, i) => (
                <div key={i} className="text-green-500/80 truncate">{log}</div>
              ))}
            </div>
          </Card>
        )}

        {!showDebugPanel && (
          <button 
            onClick={() => setShowDebugPanel(true)}
            className="fixed top-2 right-2 z-50 bg-black/80 text-green-400 p-2 rounded-full"
          >
            <Bug className="w-4 h-4" />
          </button>
        )}

        {/* Control Panel */}
        <Card className="max-w-lg p-4 mb-4">
          <h1 className="text-xl font-bold mb-2">🧪 Tour Preview v3.0 (Isolated)</h1>
          <p className="text-muted-foreground text-sm mb-4">
            Embedded tour that bypasses FirstTimeAppTour's route/auth restrictions.
          </p>
          
          <div className="flex gap-2 flex-wrap mb-4">
            <Button 
              onClick={startTour} 
              disabled={tourActive}
              size="sm"
            >
              <Play className="w-4 h-4 mr-1" />
              Start Tour
            </Button>
            
            <Button 
              onClick={runAudit}
              variant="secondary"
              size="sm"
            >
              <CheckCircle className="w-4 h-4 mr-1" />
              Audit Targets
            </Button>
            
            <Button 
              onClick={resetTour} 
              variant="outline"
              size="sm"
            >
              <RotateCcw className="w-4 h-4 mr-1" />
              Reset
            </Button>
            
            <Button 
              onClick={() => navigate('/')} 
              variant="ghost"
              size="sm"
            >
              <Home className="w-4 h-4 mr-1" />
              Home
            </Button>
          </div>
          
          {/* Visual click counter */}
          <div className="bg-primary text-primary-foreground p-3 rounded text-center">
            <span className="text-3xl font-bold">{clickCount}</span>
            <span className="text-sm ml-2 opacity-80">clicks registered</span>
          </div>
        </Card>

        {/* Target Audit Results */}
        {targetAudit && (
          <Card className="max-w-lg p-4 mb-4">
            <h2 className="font-bold mb-2 flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              Target Audit Results
            </h2>
            <div className="space-y-1 text-sm">
              {targetAudit.map((item, i) => (
                <div key={i} className={`flex items-center gap-2 p-1 rounded ${item.found ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
                  {item.found ? (
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  ) : (
                    <AlertTriangle className="w-4 h-4 text-red-500" />
                  )}
                  <span className="font-medium">{item.step}</span>
                  <span className="text-muted-foreground text-xs truncate">{item.target}</span>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
      
      {/* EMBEDDED TOUR OVERLAY */}
      <AnimatePresence>
        {tourActive && (
          <>
            <SpotlightOverlay 
              targetSelector={step.target} 
              isCenter={step.position === 'center'}
            />
            <TourCard
              step={step}
              stepIndex={currentStep}
              totalSteps={TOUR_STEPS.length}
              onNext={nextStep}
              onPrev={prevStep}
              onClose={closeTour}
              isFirst={currentStep === 0}
              isLast={currentStep === TOUR_STEPS.length - 1}
            />
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
